import { useEffect, useState } from "react";
import { ShieldCheck, Check, X } from "lucide-react";
import { toast } from "sonner";
import { appApi, formatApiErrorDetail, fmtDateTime } from "@/lib/api";

export default function KycQueue() {
  const [queue, setQueue] = useState(null);
  const [reasons, setReasons] = useState({});
  const [busy, setBusy] = useState("");

  const load = () => appApi.get("admin/kyc/pending").then((d) => setQueue(d.queue || [])).catch(() => setQueue([]));
  useEffect(() => { load(); }, []);

  const act = async (item, action) => {
    const uid = item.user_id || item.id;
    setBusy(uid);
    try {
      if (action === "approve") {
        await appApi.post(`admin/kyc/${uid}/approve`);
        toast.success(`KYC approved for ${item.email || uid}`);
      } else {
        const reason = reasons[uid] || "";
        if (!reason.trim()) {
          toast.error("Add a rejection reason first.");
          setBusy("");
          return;
        }
        await appApi.post(`admin/kyc/${uid}/reject`, { reason });
        toast.success(`KYC rejected for ${item.email || uid}`);
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="kyc-queue-page" className="space-y-6">
      <div>
        <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Admin</p>
        <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">KYC Queue</h1>
        <p className="text-sm text-zinc-500 mt-2">Approve or reject merchant identity documents (PAN, Aadhaar, bank proof).</p>
      </div>

      <div className="space-y-4" data-testid="kyc-queue-list">
        {(queue || []).map((item) => {
          const uid = item.user_id || item.id;
          return (
            <div key={uid} className="border-2 border-obsidian bg-white p-6" data-testid={`kyc-card-${uid}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 bg-obsidian text-sky-300 flex items-center justify-center"><ShieldCheck className="w-5 h-5" /></span>
                  <div>
                    <p className="font-bold">{item.name || "—"} <span className="text-zinc-400 font-normal">· {item.business || ""}</span></p>
                    <p className="text-xs font-mono text-zinc-500">{item.email} · submitted {fmtDateTime(item.submitted_at || item.created_at)}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => act(item, "approve")} disabled={busy === uid} data-testid={`kyc-approve-${uid}`}
                    className="inline-flex items-center gap-1.5 bg-acid text-white text-xs font-bold uppercase tracking-widest px-4 py-2.5 border-2 border-obsidian hover:bg-obsidian hover:text-sky-300 transition-colors duration-150 disabled:opacity-60">
                    <Check className="w-3.5 h-3.5" /> Approve
                  </button>
                  <button onClick={() => act(item, "reject")} disabled={busy === uid} data-testid={`kyc-reject-${uid}`}
                    className="inline-flex items-center gap-1.5 bg-red-600 text-white text-xs font-bold uppercase tracking-widest px-4 py-2.5 border-2 border-obsidian hover:bg-red-700 transition-colors duration-150 disabled:opacity-60">
                    <X className="w-3.5 h-3.5" /> Reject
                  </button>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3 mt-4 text-xs font-mono text-zinc-600">
                {item.pan && <p>PAN: <span className="font-bold">{item.pan}</span></p>}
                {item.aadhaar && <p>Aadhaar: <span className="font-bold">{item.aadhaar}</span></p>}
                {item.gstin && <p>GSTIN: <span className="font-bold">{item.gstin}</span></p>}
              </div>
              <input
                value={reasons[uid] || ""}
                onChange={(e) => setReasons((r) => ({ ...r, [uid]: e.target.value }))}
                placeholder="Rejection reason (required to reject)"
                data-testid={`kyc-reason-${uid}`}
                className="mt-4 w-full max-w-md border-2 border-obsidian bg-paper px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian"
              />
            </div>
          );
        })}
        {queue && queue.length === 0 && (
          <div className="border-2 border-obsidian bg-white p-10 text-sm text-zinc-500" data-testid="kyc-empty">
            Queue clear — no KYC submissions waiting.
          </div>
        )}
        {!queue && <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">Loading queue…</p>}
      </div>
    </div>
  );
}
